"use client";

import { AlertTriangle } from "lucide-react";

import type { MarkdownParseWarning } from "@/lib/content/markdown";
import { accentVars } from "@/lib/theme/accent-utils";
import { typographyStyles } from "@/lib/theme/tokens";
import { cn } from "@/lib/utils";

type MarkdownWarningsProps = {
  warnings?: MarkdownParseWarning[] | null;
  className?: string;
};

export function MarkdownWarnings({ warnings, className }: MarkdownWarningsProps) {
  if (!warnings || warnings.length === 0) {
    return null;
  }

  const warningAccent = accentVars.warning;

  return (
    <div
      role="status"
      className={cn(
        "space-y-2 rounded-[var(--radius-xxl)] border p-4 shadow-[var(--shadow-xs)]",
        className,
      )}
      style={{
        borderColor: warningAccent.border,
        backgroundColor: warningAccent.surface,
      }}
    >
      <div className="flex items-center gap-2" style={typographyStyles.detail}>
        <AlertTriangle
          className="h-4 w-4"
          aria-hidden
          style={{ color: warningAccent.strong }}
        />
        <span
          className="uppercase tracking-[0.32em]"
          style={{ color: warningAccent.foreground_muted }}
        >
          Markdown warnings
        </span>
      </div>
      <ul
        className="space-y-1 text-sm"
        style={{ color: warningAccent.foreground_muted }}
      >
        {warnings.map((warning, index) => (
          <li key={`${warning.code}-${warning.line}-${index}`} className="leading-relaxed">
            <span className="font-semibold">Line {warning.line}:</span> {warning.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
